import React, {useId, useRef, useState} from 'react';

import AemEditableText from '@/components/Aem/AemEditableText/AemEditableText';
import {AppButton} from '@/components/Button';
import CloseButton from '@/components/CloseButton/CloseButton';
import {TickCircle, WarningIcon} from '@/components/Icons';
import {StickyHeader} from '@/components/Layout';
import PopupSticked from '@/components/Popup/PopupSticked';
import {
    BROWSER_NOT_COMPATIBLE_PAGE_HEADER_TITLE,
    BROWSER_NOT_COMPATIBLE_START_BUTTON_ARIA_LABEL,
} from '@/consts/localization/localizationKeys';
import routePaths from '@/consts/route/routePaths';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import useExternalLink from '@/hooks/useExternalLink';
import useModalRoute from '@/hooks/useModalRoute';
import useScrollToPreviousPosition from '@/hooks/useScrollToPreviousPosition';
import {getAriaLabel} from '@/services/accessibilityService';
import amplitudeDeviceTracking from '@/services/analytics/amplitude/amplitudeDeviceTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import modalService from '@/services/modalService';
import {markSsoLinkUsed} from '@/services/sso/ssoLinkUsage';
import navigatorService from '@/services/user/navigatorService';
import userBrowserService from '@/services/user/userBrowserService';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

const withBluefyLink = (link) => `https://bluefy.app/pwa?site=${link}`;

const BrowserNotCompatiblePopup = ({
    modalType,
    link,
    externalLink,
    ssoTemplate,
    ssoParams,
    linkProcessor,
    deviceType,
    deviceSerialNumber,
    ssoLinkKey,
    isSsoCookiesForced = false,
}) => {
    const [ref, setRef] = useState();
    const popupRef = useRef(null);
    const headerId = useId();
    const isMobileLayout = useIsMobileLayout();
    const localizedStrings = getLocalizedStrings();
    const closeModal = () => modalService.hideModal(modalType);
    const {onClose} = useModalRoute({modalType, closeModal});
    // Use useExternalLink to get new link if it was refreshed after expiration or use
    const {link: linkFromState} = useExternalLink({
        externalLink,
        ssoTemplate,
        ssoParams,
        openInBluefy: true,
        linkProcessor,
        disabled: !ssoTemplate,
    });
    const linkToRedirect = ssoTemplate ? linkFromState || link : link;
    const finalLink = userBrowserService.isBluefyBrowser() ? linkToRedirect : withBluefyLink(linkToRedirect);

    const onClick = () => {
        amplitudeDeviceTracking.trackStartWithBluefyClick(deviceType, deviceSerialNumber);
        googleAnalyticsService.trackStartWithBluefyClick(deviceType, deviceSerialNumber);
        navigatorService.copyToClipboard(finalLink);
        markSsoLinkUsed({ssoLinkKey, openInBluefy: true, isSsoCookiesForced});
    };

    useScrollToPreviousPosition();

    // shouldReturnFocusAfterClose is set to false because after closing of the popup it breaks Home page layout
    // if click was done on the NOT first carousel item
    return (
        <PopupSticked
            shouldReturnFocusAfterClose={false}
            onClose={onClose}
            isCloseVisible={!isMobileLayout}
            className='z-[102] flex flex-col rounded-none rounded-t-xl bg-romance px-0 pb-0 pt-0 ml:rounded-xl ml:px-8 ml:pt-18'
            contentRef={(node) => {
                popupRef.current = node;
                setRef(node);
            }}
            aria-labelledby={headerId}
        >
            <WarningBannersTop
                className='px-6 ml:px-0'
                source={WARNING_BANNER_SOURCE_TYPES.BROWSER_NOT_COMPATIBLE_POPUP}
                containerRef={popupRef}
            />
            {isMobileLayout && (
                <StickyHeader
                    scrollElement={ref}
                    endIcon={<CloseButton onClick={onClose} />}
                    title={localizedStrings[BROWSER_NOT_COMPATIBLE_PAGE_HEADER_TITLE]}
                    className='sticky top-0'
                />
            )}
            <div className='px-6 pb-8 ml:px-0 ml:pb-0'>
                <WarningIcon className='mx-auto h-11 w-11 shrink-0' />
                <h1 id={headerId} className='mb-0 mt-2 text-28'>
                    <AemEditableText
                        className='p-0'
                        pageName={routePaths.MODALS}
                        itemName='browserNotCompatibleTitle'
                    />
                </h1>
                <h2 className='mb-0 mt-8 text-left text-20 rtl:text-right'>
                    <AemEditableText pageName={routePaths.MODALS} itemName='browserNotCompatibleSubtitle' />
                </h2>
                <div className='mt-4 rounded-xl bg-primary-dark-5 p-4'>
                    <div className='flex flex-col gap-4'>
                        {['browserNotCompatibleStep1', 'browserNotCompatibleStep2', 'browserNotCompatibleStep3'].map(
                            (itemName) => (
                                <AemEditableText
                                    key={itemName}
                                    pageName={routePaths.MODALS}
                                    itemName={itemName}
                                    render={(text) => {
                                        return text ? <InstructionStep text={text} /> : null;
                                    }}
                                />
                            )
                        )}
                    </div>
                </div>
                <AppButton
                    className='mb-4 mt-8 w-full shrink-0'
                    primary
                    light
                    isExternalLink
                    to={finalLink}
                    aria-label={getAriaLabel(BROWSER_NOT_COMPATIBLE_START_BUTTON_ARIA_LABEL)}
                    onClick={onClick}
                    isTargetBlank
                >
                    <AemEditableText pageName={routePaths.MODALS} itemName='browserNotCompatibleButton' />
                </AppButton>
                <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.BROWSER_NOT_COMPATIBLE_POPUP} />
            </div>
        </PopupSticked>
    );
};

const InstructionStep = ({text}) => {
    return (
        <div className='flex items-center justify-start gap-2 text-14 tracking-04'>
            <TickCircle className='flex-shrink-0' />
            <span className='text-start'>{text}</span>
        </div>
    );
};

export default BrowserNotCompatiblePopup;
